import axios from 'axios';

import '@/config/axios';
import { readMessageFromData } from '@/utils/apiErrorMessage';
import { isRecord } from '@/utils/typeGuards';

import type { AppNavigate } from '@/hooks/useUrlState';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';

/** Backend REST root with trailing slash, e.g. `https://api.example.com/api/v1/`. */
export const API_BASE_URL = import.meta.env.VITE_API_URL;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly data: unknown,
    readonly errorCode?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function readErrorMessage(data: unknown, fallback: string): string {
  return readMessageFromData(data) ?? fallback;
}

function readErrorCode(data: unknown): string | undefined {
  if (typeof data === 'object' && data && 'error_code' in data) {
    return String(data.error_code);
  }
  return undefined;
}

async function apiRequest<T>(config: AxiosRequestConfig): Promise<T> {
  try {
    const res = await axios(config);
    return res.data as T;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;
      const data: unknown = error.response.data;
      throw new ApiError(
        readErrorMessage(data, error.message),
        status,
        data,
        readErrorCode(data),
      );
    }
    throw error;
  }
}

export const apiGet = <T>(url: string, config?: AxiosRequestConfig) =>
  apiRequest<T>({ ...config, method: 'GET', url });

export const apiPost = <T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
) => apiRequest<T>({ ...config, method: 'POST', url, data });

export const apiPut = <T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
) => apiRequest<T>({ ...config, method: 'PUT', url, data });

export const apiPatch = <T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
) => apiRequest<T>({ ...config, method: 'PATCH', url, data });

export const apiDelete = <T>(url: string, config?: AxiosRequestConfig) =>
  apiRequest<T>({ ...config, method: 'DELETE', url });

/** GET that returns undefined on error (optional redirect on not-found). */
export async function apiGetOptional<T>(
  url: string,
  config?: AxiosRequestConfig,
  navigate?: AppNavigate,
): Promise<T | undefined> {
  try {
    return await apiGet<T>(url, config);
  } catch (error) {
    redirectOnResourceNotFound(error, navigate);
    return undefined;
  }
}

/** GET with blob response body. */
export async function apiGetBlob(
  url: string,
  config?: AxiosRequestConfig,
): Promise<Blob> {
  return apiRequest<Blob>({
    ...config,
    method: 'GET',
    url,
    responseType: 'blob',
  });
}

/** Read axios error response body when present. */
export const getAxiosErrorData = (error: unknown): unknown => {
  if (!axios.isAxiosError(error)) return undefined;
  return error.response?.data;
};

/** Narrow axios error response body to a record. */
export const getAxiosErrorRecord = (
  error: unknown,
): Record<string, unknown> | undefined => {
  const data = getAxiosErrorData(error);
  return isRecord(data) ? data : undefined;
};

/** Return axios error response when present. */
export const getAxiosErrorResponse = <T = unknown>(
  error: unknown,
): AxiosResponse<T> | undefined => {
  if (axios.isAxiosError(error)) {
    return error.response as AxiosResponse<T> | undefined;
  }
  return undefined;
};

function isResourceNotFoundError(error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.errorCode === 'resource_not_found') {
      return true;
    }
    return (
      isRecord(error.data) && error.data.error_code === 'resource_not_found'
    );
  }
  return getAxiosErrorRecord(error)?.error_code === 'resource_not_found';
}

/**
 * On resource_not_found, call urlBase-aware `navigate('/not-found')`.
 * Pass the navigate from `useUrlState()` so embed/plugin stay under their pack
 * prefix (e.g. `/embed/:pack/not-found` → splat NotFound). Omit navigate to
 * suppress redirects (notification deep links, soft lookups).
 */
export const redirectOnResourceNotFound = (
  error: unknown,
  navigate?: AppNavigate,
): void => {
  if (navigate && isResourceNotFoundError(error)) {
    navigate('/not-found');
  }
};
